<template>
  <modal-dialog
    ref="dialog"
    :visible="dialog"
    content-class="dialog-height-60"
    card-class="pa-0 pb-5 mb-5"
    persistent
    scrollable
    max-width="600px"
    :title="$t('title.setup_tables')"
  >
    <!-- Data Table -->
    <validation-observer ref="observer">
      <v-data-table
        v-reorder-table
        :headers="headers"
        :items="tables"
        disable-pagination
        hide-default-footer
        :no-data-text="$t('label.no_tables')"
        @sorted="changeOrder"
      >
        <template #item.id>
          <v-icon
            class="reorder-handle"
            small
          >
            mdi-arrow-up-down
          </v-icon>
        </template>
        <template #item.name="props">
          <!-- Pop Up Editor -->
          <data-table-input-dialog
            v-model="props.item['no']"
            :value.sync="props.item['no']"
            rules="required|min:1|max:5"
          />
        </template>
        <template #item.seats="props">
          <!-- Pop Up Editor -->
          <data-table-input-dialog
            v-model="props.item['seats']"
            type="select"
            :items="seats"
            :value.sync="props.item['seats']"
          />
        </template>
        <template #item.actions="props">
          <v-btn
            icon
            @click="remove(props.item.id)"
          >
            <v-icon small>
              mdi-delete
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </validation-observer>

    <!-- Actions -->
    <template #actions>
      <v-btn
        color="primary"
        @click="add"
      >
        {{ $t('button.add_new_table') }}
      </v-btn>
      <v-spacer />

      <v-btn
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        {{ $t('button.save') }}
      </v-btn>
      <v-btn
        color="error"
        text
        @click="cancel"
      >
        {{ $t('button.cancel') }}
      </v-btn>
    </template>
  </modal-dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import Sortable from 'sortablejs';
import DialogClosable from '@/mixins/dialog-closable';
import { performSaveAction } from '@/services/vue-utils';
import { isModelEqualToModel, cloneModel } from '@/model/model-utils';
import DataTableInputDialog from '@/components/dialogs/DataTableInputDialog.vue';
import Tab from '@/model/Tab';
import TabItem, { suggestedTabItem } from '@/model/TabItem';

export default Vue.extend({
  name: 'TablesDialog',
  components: { DataTableInputDialog },
  mixins: [DialogClosable],
  props: {
    tab: { type: Object as PropType<Tab>, required: true },
  },
  data() {
    return {
      tables: [] as TabItem[],
      oldTables: [] as TabItem[],
      headers: [
        {
          text: '',
          width: '32',
          value: 'id',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$i18n.t('label.table_name'),
          value: 'name',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$i18n.t('label.seats'),
          width: '80',
          value: 'seats',
          align: 'start',
          sortable: false,
        },
        {
          text: '',
          width: '32',
          value: 'actions',
          align: 'center',
          sortable: false,
        },
      ],
      seats: [...new Array(99)].map((e, i) => (i + 1)),
    };
  },
  computed: {
    isChanged(): boolean {
      return !isModelEqualToModel(this.tables, this.oldTables);
    },
  },
  watch: {
    visible() {
      this.reset();
    },
  },
  mounted() {
  },
  methods: {
    changeOrder(event: Sortable.SortableEvent) {
      if (event.oldIndex === undefined || event.newIndex === undefined) return;
      const movedItem = this.tables.splice(event.oldIndex, 1)[0];
      this.tables.splice(event.newIndex, 0, movedItem);
    },
    reset() {
      const tab = this.$tstore.getters.tabsById.get(this.tab.id) as Tab | undefined;
      this.tables = (tab?.tabItems ?? []).map((o :TabItem) => cloneModel(o));
      this.oldTables = tab?.tabItems ?? [];
    },
    add() {
      const table = suggestedTabItem(this.tables);
      this.tables.push(table);

      (this.$refs.dialog as Vue & { scrollToBottom: ()=>void }).scrollToBottom();
    },
    remove(id: number) {
      this.tables = this.tables.filter((o) => o.id !== id);
    },
    close() {
      this.$emit('close');
    },
    cancel() {
      this.close();
    },
    async save() {
      const ok = await performSaveAction(
        this.$refs.observer,
        async () => this.$tstore.dispatch('sendTabItems', {
          entities: this.tables,
          tabId: this.tab.id,
        }),
      );

      if (ok) {
        this.close();
      }
    },
  },
});
</script>
